import React, { useState } from 'react';
import { ref, update } from 'firebase/database';
import { toast } from 'react-toastify';
import {
    FiAlertTriangle,
    FiKey,
    FiLogIn,
    FiLogOut,
    FiRefreshCw,
    FiSave,
    FiX,
} from 'react-icons/fi';

import { db } from '../../firebase';
import { PROTECTED_PACKAGES } from '../../utils/appVersions';

// LicenseTab — per-device license provisioning + maintenance (storage, uninstall).
//
// IMPORTANT: the monitoring app reads mdm/devices/<MAC>/command as a plain STRING
// (String.class). So every command here is written as a raw string (e.g.
// "provision_license:XXXX-XXXX"), NOT an object. This mirrors CommandsTab.jsx.

async function sendCommand(mac, command) {
    // Same contract as the bulk handler / CommandsTab: string command + feedback pending.
    await update(ref(db, `mdm/devices/${mac}`), { command, feedback: 'pending' });
}

export default function LicenseTab({ mac, device }) {
    return (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <LicenseCard mac={mac} device={device} />
            <ManualLabelsCard mac={mac} device={device} />
        </div>
    );
}

// ---------------------------------------------------------------------
// Primitives (kept local so this tab has no cross-file dependency)
// ---------------------------------------------------------------------

function Card({ icon, title, description, children }) {
    return (
        <section className="card-chrome p-5">
            <header className="mb-4">
                <h2 className="text-base font-semibold text-brand-400 inline-flex items-center gap-2">
                    {icon} {title}
                </h2>
                {description && <p className="mt-1 text-xs text-gray-500">{description}</p>}
            </header>
            <div className="space-y-3">{children}</div>
        </section>
    );
}

function Btn({ onClick, icon, children, tone = 'neutral', disabled = false }) {
    const tones = {
        neutral: 'bg-white/[0.05] hover:bg-white/[0.08] text-gray-100',
        primary: 'bg-brand hover:bg-brand-400 text-white',
        success: 'bg-green-500/15 hover:bg-green-500/25 text-green-400',
        warning: 'bg-yellow-500/15 hover:bg-yellow-500/25 text-yellow-400',
        danger: 'bg-red-500/15 hover:bg-red-500/25 text-red-400',
    };
    return (
        <button
            onClick={onClick}
            disabled={disabled}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition disabled:opacity-40 disabled:cursor-not-allowed ${tones[tone]}`}
        >
            {icon}
            {children}
        </button>
    );
}

const inputCls =
    'w-full bg-white/[0.05] border border-white/[0.1] focus:border-brand rounded-lg px-3 py-1.5 text-sm text-gray-100 outline-none placeholder-gray-600';

// Centered confirmation dialog. Renders nothing when `open` is false, so
// callers can mount it unconditionally and just flip the flag.
function ConfirmModal({ open, title, message, confirmLabel, tone = 'danger', onConfirm, onCancel }) {
    if (!open) return null;
    const confirmTones = {
        danger: 'bg-red-500/15 hover:bg-red-500/25 text-red-400',
        primary: 'bg-brand hover:bg-brand-400 text-white',
    };
    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
            onClick={onCancel}
        >
            <div
                className="card-chrome w-full max-w-md p-5 shadow-xl"
                onClick={(e) => e.stopPropagation()}
            >
                <header className="flex items-start justify-between gap-3 mb-3">
                    <h3 className="text-base font-semibold text-gray-100 inline-flex items-center gap-2">
                        <FiAlertTriangle className="text-red-400" /> {title}
                    </h3>
                    <button
                        onClick={onCancel}
                        className="text-gray-500 hover:text-gray-200 transition"
                        aria-label="Close"
                    >
                        <FiX />
                    </button>
                </header>
                <p className="text-sm text-gray-400 leading-relaxed">{message}</p>
                <div className="flex justify-end gap-2 pt-5">
                    <button
                        onClick={onCancel}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-white/[0.05] hover:bg-white/[0.08] text-gray-100 transition"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition ${confirmTones[tone] || confirmTones.danger}`}
                    >
                        <FiLogOut /> {confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
}

function Field({ label, value, mono, badge }) {
    return (
        <div>
            <div className="text-xs text-gray-500">{label}</div>
            {badge ? (
                <span
                    className={`inline-block mt-0.5 px-2 py-0.5 rounded text-xs font-medium ${
                        badge === 'active'
                            ? 'bg-green-500/15 text-green-400'
                            : 'bg-red-500/15 text-red-400'
                    }`}
                >
                    {value}
                </span>
            ) : (
                <div className={`text-gray-200 ${mono ? 'font-mono' : ''}`}>{value}</div>
            )}
        </div>
    );
}

// ---------------------------------------------------------------------
// License — provision (login) / deprovision (logout) / sync
// ---------------------------------------------------------------------

function LicenseCard({ mac, device }) {
    const [code, setCode] = useState('');
    const [confirmLogout, setConfirmLogout] = useState(false);
    const status = device?.license_status;
    const badge = status === 'active' ? 'active' : status === 'revoked' ? 'revoked' : null;

    const provision = async () => {
        const c = code.trim().toUpperCase();
        if (!c) {
            toast.warning('Enter a license code');
            return;
        }
        try {
            await sendCommand(mac, `provision_license:${c}`);
            toast.success(`Provisioning ${c}…`);
        } catch (e) {
            toast.error(`Failed: ${e.message}`);
        }
    };
    const deprovision = async () => {
        setConfirmLogout(false);
        try {
            await sendCommand(mac, 'deprovision_license');
            toast.success('Logout (deprovision) sent');
        } catch (e) {
            toast.error(`Failed: ${e.message}`);
        }
    };
    const sync = async () => {
        try {
            await sendCommand(mac, 'sync_license');
            toast.success('Sync requested');
        } catch (e) {
            toast.error(`Failed: ${e.message}`);
        }
    };

    return (
        <Card
            icon={<FiKey />}
            title="License"
            description="Login provisions the license (device runs its normal login); Logout deprovisions it. Sync re-reads the current license from the player."
        >
            {/* 3-column grid keeps everything in two rows (no vertical growth). The third
                column holds the extra auto fields read from the player: the person's name
                (first + last) sits to the right of Status, business to the right of Customer. */}
            <div className="grid grid-cols-3 gap-3 text-sm">
                <Field label="License" value={device?.license_code || '—'} mono />
                <Field label="Status" value={status || '—'} badge={badge} />
                <Field
                    label="Name (auto)"
                    value={[device?.first_name, device?.last_name].filter(Boolean).join(' ') || '—'}
                />
                <Field label="Device name (auto)" value={device?.name || '—'} />
                <Field label="Customer (auto)" value={device?.customer_name || '—'} />
                <Field label="Business (auto)" value={device?.business_name || '—'} />
            </div>
            <div className="flex flex-wrap items-center gap-2 pt-2">
                <input
                    value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                    placeholder="XXXX-XXXX"
                    className={`${inputCls} flex-1 min-w-[140px] font-mono uppercase`}
                />
                <Btn onClick={provision} icon={<FiLogIn />} tone="success">
                    Login
                </Btn>
                <Btn onClick={() => setConfirmLogout(true)} icon={<FiLogOut />} tone="danger">
                    Logout
                </Btn>
                <Btn onClick={sync} icon={<FiRefreshCw />}>
                    Sync
                </Btn>
            </div>

            <ConfirmModal
                open={confirmLogout}
                title="Deprovision license?"
                message={
                    `This sends a Logout (deprovision) command to ${device?.name || mac}. ` +
                    `The player will log out and stop playback until a license is provisioned again. Continue?`
                }
                confirmLabel="Logout device"
                tone="danger"
                onConfirm={deprovision}
                onCancel={() => setConfirmLogout(false)}
            />
        </Card>
    );
}

// ---------------------------------------------------------------------
// Manual labels — operator-entered names, NOT overwritten by the auto values
// ---------------------------------------------------------------------

function ManualLabelsCard({ mac, device }) {
    const [dn, setDn] = useState(device?.manual_device_name || '');
    const [cn, setCn] = useState(device?.manual_customer_name || '');
    const [saving, setSaving] = useState(false);

    const save = async () => {
        try {
            setSaving(true);
            await update(ref(db, `mdm/devices/${mac}`), {
                manual_device_name: dn.trim(),
                manual_customer_name: cn.trim(),
            });
            toast.success('Manual labels saved');
        } catch (e) {
            toast.error(`Failed: ${e.message}`);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Card
            icon={<FiSave />}
            title="Manual labels"
            description="Operator-entered device / customer name. Independent of the auto values pulled from the player — automation never overwrites these."
        >
            <label className="block text-xs text-gray-500">Device name (manual)</label>
            <input value={dn} onChange={(e) => setDn(e.target.value)} className={inputCls} placeholder="e.g. Kafic Centar - TV1" />
            <label className="block text-xs text-gray-500 pt-1">Customer name (manual)</label>
            <input value={cn} onChange={(e) => setCn(e.target.value)} className={inputCls} placeholder="e.g. Jasko" />
            <div className="pt-2">
                <Btn onClick={save} icon={<FiSave />} tone="primary" disabled={saving}>
                    {saving ? 'Saving…' : 'Save'}
                </Btn>
            </div>
        </Card>
    );
}

